import React from 'react';
import { Layout, Plus, X } from 'lucide-react';

const AlvWizard = ({ data, setData }) => {
  const options = [
    { id: 'zebra', label: 'Patrón de Cebra (Striped)', desc: 'Filas alternadas para mejor legibilidad' },
    { id: 'toolbar', label: 'Barra de Herramientas Estándar', desc: 'Excel, Print, Filter, Sort activados' },
    { id: 'optimize', label: 'Optimizar Ancho de Columnas', desc: 'set_optimize( abap_true ) automático' },
    { id: 'editable', label: 'ALV Editable (Grid OO)', desc: 'Permite edición inline de registros' },
  ];

  const agregarHotspot = () => {
    const hs = { id: Date.now(), campo: '', etiqueta: '' };
    setData({ ...data, alvHotspots: [...(data.alvHotspots || []), hs] });
  };

  const actualizarHotspot = (id, campo, valor) => {
    setData({
      ...data,
      alvHotspots: (data.alvHotspots || []).map(h =>
        h.id === id ? { ...h, [campo]: valor } : h
      ),
    });
  };

  const eliminarHotspot = (id) => {
    setData({ ...data, alvHotspots: (data.alvHotspots || []).filter(h => h.id !== id) });
  };

  const inputClass = "w-full p-2.5 bg-slate-800 border border-slate-700 rounded-xl text-xs font-mono text-white placeholder:text-slate-600 outline-none focus:border-blue-400 uppercase";
  const selectClass = "w-full p-2.5 bg-slate-800 border-none rounded-xl text-xs font-bold text-white outline-none focus:ring-2 focus:ring-blue-500";
  const labelClass = "text-[9px] font-bold text-slate-400 uppercase block mb-1";

  return (
    <div className="animate-in fade-in slide-in-from-top-4 duration-500 text-left space-y-6">

      {/* NOTA */}
      <div className="bg-blue-900/10 border-l-4 border-blue-600 p-5 rounded-r-3xl flex gap-4 items-start">
        <div className="bg-blue-600 p-2 rounded-xl text-white shrink-0">
          <Layout size={18} />
        </div>
        <div>
          <p className="text-[11px] text-blue-400 font-black uppercase tracking-widest mb-1">
            ALV Report Engine Guide
          </p>
          <p className="text-[11px] text-slate-400 leading-relaxed">
            Esta herramienta actúa como un <strong>Senior ABAP Reporting Specialist</strong>.
            Ingresá la tabla o estructura del diccionario. El sistema generará el catálogo
            de campos automáticamente y estructurará un programa completo basado en
            <strong> SALV</strong> o <strong>Grid OO</strong>, con manejo de eventos y
            nota técnica de implementación detallada.
          </p>
        </div>
      </div>

      {/* FILA 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* COLUMNA 1 */}
        <div className="bg-[#0F172A] p-6 rounded-[28px] text-white border-b-4 border-blue-500 space-y-4">
          <label className="text-[10px] font-black text-blue-400 uppercase flex items-center gap-2 border-b border-slate-800 pb-2">
            <Layout size={14} /> Configuración Base
          </label>

          <div>
            <label className={labelClass}>Fuente de Datos</label>
            <div className="flex gap-2 mb-3">
              {[
                { val: 'tabla', label: 'Tabla Física' },
                { val: 'cds', label: 'Vista CDS' },
              ].map((op) => (
                <button
                  key={op.val}
                  onClick={() => setData({ ...data, alvFuente: op.val })}
                  className={`flex-1 py-2 rounded-xl text-[10px] font-black uppercase border transition-all ${(data.alvFuente || 'tabla') === op.val
                      ? 'bg-blue-600/20 border-blue-500/50 text-blue-400'
                      : 'bg-slate-800 border-slate-700 text-slate-500 hover:border-slate-600'
                    }`}
                >
                  {op.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className={labelClass}>
              {(data.alvFuente || 'tabla') === 'cds'
                ? 'Nombre Vista CDS (ZI_xxx o ZC_xxx)'
                : 'Tabla o Estructura Diccionario'}
            </label>
            <input type="text" className={inputClass}
              placeholder={(data.alvFuente || 'tabla') === 'cds'
                ? 'Ej: ZC_PurchaseOrder o ZI_SalesOrder'
                : 'Ej: MARA, VBAK o ZESTRUCT_ALV'}
              value={data.alvTable || ''}
              onChange={(e) => setData({ ...data, alvTable: e.target.value })} />
          </div>

          {(data.alvFuente || 'tabla') === 'cds' && (
            <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-xl">
              <p className="text-[9px] text-blue-400 leading-relaxed">
                El ALV leerá directamente desde la vista CDS sin acceder a tablas físicas.
                Asegurate que la vista esté activada en ADT antes de ejecutar.
              </p>
            </div>
          )}

          <div>
            <label className={labelClass}>Tecnología ALV</label>
            <select className={selectClass}
              value={data.alvType || 'SALV'}
              onChange={(e) => setData({ ...data, alvType: e.target.value })}>
              <option value="SALV">SALV (Object Oriented - New)</option>
              <option value="OO">CL_GUI_ALV_GRID (Container based)</option>
              <option value="REUSE">REUSE_ALV_GRID_DISPLAY (Classic)</option>
            </select>
          </div>

          <div>
            <label className={labelClass}>Título del Reporte (Header)</label>
            <input type="text"
              className="w-full p-2.5 bg-slate-800 border border-slate-700 rounded-xl text-xs text-white placeholder:text-slate-600 outline-none focus:border-blue-400"
              placeholder="Ej: Listado de Facturas Pendientes"
              value={data.alvTitle || ''}
              onChange={(e) => setData({ ...data, alvTitle: e.target.value })} />
          </div>
        </div>

        {/* COLUMNA 2 */}
        <div className="bg-[#0F172A] p-6 rounded-[28px] text-white border-b-4 border-blue-500 space-y-4">
          <label className="text-[10px] font-black text-blue-400 uppercase flex items-center gap-2 border-b border-slate-800 pb-2">
            <Layout size={14} /> Funcionalidades
          </label>

          <div className="space-y-3">
            {options.map((opt) => (
              <button key={opt.id}
                onClick={() => setData({
                  ...data,
                  alvOptions: { ...data.alvOptions, [opt.id]: !data.alvOptions?.[opt.id] }
                })}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${data.alvOptions?.[opt.id]
                    ? 'bg-blue-600/20 border-blue-500/50'
                    : 'bg-slate-800 border-slate-700 hover:border-slate-600'
                  }`}>
                <div className={`w-2 h-2 rounded-full shrink-0 ${data.alvOptions?.[opt.id] ? 'bg-blue-400 animate-pulse' : 'bg-slate-600'}`} />
                <div className="flex-1">
                  <p className={`text-[10px] font-black uppercase ${data.alvOptions?.[opt.id] ? 'text-blue-400' : 'text-slate-400'}`}>
                    {opt.label}
                  </p>
                  <p className="text-[9px] text-slate-500">{opt.desc}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* HOTSPOTS */}
      <div className="bg-[#0F172A] p-6 rounded-[28px] text-white border-b-4 border-blue-500">
        <div className="flex items-center justify-between border-b border-slate-800 pb-3 mb-4">
          <label className="text-[10px] font-black text-blue-400 uppercase flex items-center gap-2">
            <Layout size={14} /> Configuración de Campos Hotspot
          </label>
          <button onClick={agregarHotspot}
            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600/20 border border-blue-500/30 rounded-xl text-blue-400 text-[10px] font-black uppercase hover:bg-blue-600/30 transition-all">
            <Plus size={12} /> Agregar Hotspot
          </button>
        </div>

        {(data.alvHotspots || []).length === 0 ? (
          <p className="text-[10px] text-slate-600 italic text-center py-4">
            No hay hotspots definidos — hacé click en Agregar Hotspot
          </p>
        ) : (
          <div className="space-y-3">
            {(data.alvHotspots || []).map((hs) => (
              <div key={hs.id} className="flex items-center gap-3 bg-slate-800/50 border border-slate-700 rounded-xl p-3">
                <div className="flex-1 grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>Campo Técnico</label>
                    <input type="text" className={inputClass} placeholder="E.g. EBELN"
                      value={hs.campo}
                      onChange={(e) => actualizarHotspot(hs.id, 'campo', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Etiqueta de Usuario</label>
                    <input type="text"
                      className="w-full p-2.5 bg-slate-800 border border-slate-700 rounded-xl text-xs text-white placeholder:text-slate-600 outline-none focus:border-blue-400"
                      placeholder="E.g. Nro Pedido"
                      value={hs.etiqueta}
                      onChange={(e) => actualizarHotspot(hs.id, 'etiqueta', e.target.value)} />
                  </div>
                </div>
                <button onClick={() => eliminarHotspot(hs.id)}
                  className="text-slate-600 hover:text-red-400 transition-colors shrink-0">
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AlvWizard;